/**
 * @file api-catalog.ts
 * @description Клиент `/admin/catalog/*` для @chaiberi/admin (phase07).
 *
 * Зеркало контроллеров apps/api/src/catalog/*. Все методы возвращают `data`
 * напрямую (apiFetch разворачивает `{data}/{error}`).
 */

import type { CertificateTypeKey } from "@chaiberi/shared";

import { apiDownload, apiFetch } from "./api-client";
import { uploadWithProgress } from "./upload-with-progress";
import { tabContextHeaders } from "./tab-context";
import type { SalesChannelKind } from "./api-channel-listings";
import type { EffectiveKind, LabelTemplate } from "./api-product-kinds";
import type { SiteRedirectChange } from "./api-site-redirects";
import type { SeoKeywordsDto, SeoKeywordsInput } from "./site-seo/keywords";

/** Р3в (SB131): поля запроса и ответа о переадресации при смене адреса (контракт r3v §3). */
export interface RedirectOnSlugInput {
  /** Переадресовать со старого адреса, если он был живым; нет — да. */
  redirectFromOldSlug?: boolean;
}
export interface RedirectOnSlugResult {
  redirect?: SiteRedirectChange | null;
}

// ─── Categories ──────────────────────────────────────────────────────────

export type MarkingCategory = "BAD" | "PERFUME_COSMETICS" | "OTHER";

export interface CatalogCategory extends SeoKeywordsDto, RedirectOnSlugResult {
  id: string;
  shopId: string | null;
  parentId: string | null;
  slug: string;
  displayName: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  markingEnabled: boolean;
  markingCategory: MarkingCategory | null;
  markingStartDate: string | null;
  tnvedHint: string | null;
  /** phase14.0 — внешняя категория-якорь (Avito/VK/Ozon/WB). */
  isExternal: boolean;
  externalSource: "AVITO" | "VK" | "OZON" | "WB" | null;
  /** phase14.0.2 — системная корневая (нельзя редактировать). */
  isSystemRoot: boolean;
  /** phase14.0.2 — tristate-флаг публикации (null = наследовать от parent). */
  isPublishedOnSite: boolean | null;
  /** phase18 — фото категории (hero страницы категории на сайте). */
  imageAlt: string | null;
  /** Presigned URL фото (TTL 1 час) — для превью. NULL — фото нет. */
  imageUrl: string | null;
  /** phase19.16 Р2а (§19.8): фото из медиатеки магазина; `null` — нет или прежнее фото. */
  imageMediaId?: string | null;
  /** Точка фокуса фото в % (плитка 1:1 и баннер режутся по ней); `null` — центр. */
  imageFocus?: { x: number; y: number } | null;
  /** Товаров магазина вкладки в самой категории (контракт §20). */
  productsCount?: number;
  /** Товаров вместе со всеми подкатегориями, видимыми сотруднику (контракт §20). */
  productsCountTotal?: number;
  /** phase19.22 (W22) — свой вид товара по умолчанию; null — как у родителя. */
  productKindId: string | null;
  /** Вид от предков без учёта своего: подпись «Как у родителя (Пищевой)». */
  inheritedKind?: { id: string; name: string } | null;
}

export interface CreateCategoryInput extends SeoKeywordsInput {
  parentId?: string;
  slug?: string;
  displayName: string;
  description?: string;
  sortOrder?: number;
  /** phase19.22 (W22) — вид по умолчанию; null — как у родителя. */
  productKindId?: string | null;
}

export interface UpdateCategoryInput extends SeoKeywordsInput, RedirectOnSlugInput {
  parentId?: string | null;
  slug?: string;
  displayName?: string;
  description?: string;
  sortOrder?: number;
  isActive?: boolean;
  /** phase14.0.2 — tristate-флаг публикации (null = наследовать). */
  isPublishedOnSite?: boolean | null;
  /** phase19.22 (W22) — вид по умолчанию; null — как у родителя. */
  productKindId?: string | null;
  /** phase19.16 Р2а (§19.8): фото из медиатеки магазина; `null` — убрать фото. */
  imageMediaId?: string | null;
  /** Точка фокуса 0…100 — оба или оба `null`. */
  imageFocusX?: number | null;
  imageFocusY?: number | null;
}

export interface UpdateCategoryMarkingInput {
  markingEnabled: boolean;
  markingCategory?: MarkingCategory;
  markingStartDate?: string;
  tnvedHint?: string;
}

export const catalogCategories = {
  list: () => apiFetch<CatalogCategory[]>("/admin/catalog/categories"),
  findOne: (id: string) => apiFetch<CatalogCategory>(`/admin/catalog/categories/${id}`),
  create: (input: CreateCategoryInput) =>
    apiFetch<CatalogCategory>("/admin/catalog/categories", { method: "POST", json: input }),
  update: (id: string, input: UpdateCategoryInput) =>
    apiFetch<CatalogCategory>(`/admin/catalog/categories/${id}`, {
      method: "PATCH",
      json: input,
    }),
  updateMarking: (id: string, input: UpdateCategoryMarkingInput) =>
    apiFetch<CatalogCategory>(`/admin/catalog/categories/${id}/marking`, {
      method: "PATCH",
      json: input,
    }),
  remove: (id: string) =>
    apiFetch<void>(`/admin/catalog/categories/${id}`, { method: "DELETE" }),
  /** phase14.0.2 — атомарная перестановка sortOrder для нескольких категорий. */
  reorder: (items: Array<{ id: string; sortOrder: number }>) =>
    apiFetch<{ updated: number }>("/admin/catalog/categories/reorder", {
      method: "POST",
      json: { items },
    }),
  /** phase18 — удалить прежнее фото категории (до медиатеки; §19.8 — устарело, до следующего выпуска). */
  removeImage: (id: string) =>
    apiFetch<CatalogCategory>(`/admin/catalog/categories/${id}/image`, { method: "DELETE" }),
};

// ─── Tags ────────────────────────────────────────────────────────────────

export interface CatalogTag {
  id: string;
  slug: string;
  displayName: string;
  productsCount?: number;
}

export const catalogTags = {
  list: () => apiFetch<CatalogTag[]>("/admin/catalog/tags"),
  create: (input: { displayName: string; slug?: string }) =>
    apiFetch<CatalogTag>("/admin/catalog/tags", { method: "POST", json: input }),
  update: (id: string, input: { displayName?: string; slug?: string }) =>
    apiFetch<CatalogTag>(`/admin/catalog/tags/${id}`, { method: "PATCH", json: input }),
  remove: (id: string) => apiFetch<void>(`/admin/catalog/tags/${id}`, { method: "DELETE" }),
};

// ─── Units ───────────────────────────────────────────────────────────────

export interface CatalogUnit {
  id: string;
  code: string;
  displayName: string;
}

export const catalogUnits = {
  list: () => apiFetch<CatalogUnit[]>("/admin/catalog/units"),
};

// ─── Products ────────────────────────────────────────────────────────────

export type ProductStatus = "DRAFT" | "ACTIVE" | "ARCHIVED";

export interface CatalogProduct extends SeoKeywordsDto, RedirectOnSlugResult {
  id: string;
  shopId: string;
  categoryId: string;
  slug: string;
  displayName: string;
  shortDescription: string | null;
  description: string | null;
  status: ProductStatus;
  metaTitle: string | null;
  metaDescription: string | null;
  composition: string | null;
  shelfLifeDays: number | null;
  storageConditions: string | null;
  allergens: string[];
  nutritionFacts: unknown;
  badWarning: boolean;
  ageRestriction: number | null;
  notForConsumption: boolean;
  producerId: string | null;
  declarationNumber: string | null;
  attributes: unknown;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  tagIds: string[];
  skusCount: number;
  isPublished: boolean;
  /** phase14.0.2 — tristate-флаг публикации на сайте. */
  isPublishedOnSite: boolean | null;
  /** phase14.0.2 — Тип товара (для WMS/Manufacturing/стикеров). */
  itemKind:
    | "RAW_MATERIAL"
    | "WORK_IN_PROGRESS"
    | "FINISHED_GOOD"
    | "PACKAGING"
    | "SERVICE"
    | "DIGITAL"
    | "BUNDLE"
    | null;
  /** phase19.5 — публичный URL главного фото (null = фото нет → заглушка). */
  primaryImageUrl: string | null;
  /** phase19.5 — каналы с активной публикацией (для фильтров списка). */
  publishedChannels: SalesChannelKind[];
  /**
   * phase19.14 (W9) — приходит только при `withStock=1`.
   * `stockTracked=false` — складской учёт не ведётся (услуга, цифровой товар):
   * в колонке «Доступно» рисуем прочерк, а не ноль.
   */
  stockTracked?: boolean;
  /** Сумма доступного по фасовкам и sellable-складам; null — учёт не ведётся. */
  available?: string | null;
  /** Разбивка «Доступно» по фасовкам — поповер в списке товаров. */
  availableBySku?: SkuAvailabilityDto[];
  /** phase19.22 (W22) — свой вид товара; null — как в категории. */
  productKindId: string | null;
  /** Действующий вид (свой или от категории); null — вид не задан. */
  effectiveKind: EffectiveKind | null;
  /**
   * phase19.31 (SC49, SC51) — разброс цен активных фасовок, деньги строкой
   * («250.00»); только в списке товаров. null — фасовок нет или цену скрывает
   * роль (кладовщик цен продажи не видит, контракт §11).
   */
  priceMin?: string | null;
  priceMax?: string | null;
  /**
   * phase19.31 (контракт §20) — артикулы всех фасовок, действующие первыми;
   * приходит в списке с `withStock` и без; без фасовок — `[]`.
   */
  skuCodes?: string[];
}

/** Доступность одной фасовки (`GET /admin/catalog/products?withStock=1`). */
export interface SkuAvailabilityDto {
  skuId: string;
  code: string;
  displayName: string | null;
  quantity: string;
  reserved: string;
  available: string;
  /** phase19.14 (W9) — порог «мало» с карточки фасовки; null — не задан. */
  minStock: string | null;
  /** `0 < доступно ≤ minStock`. При пустом пороге всегда false. */
  isLow: boolean;
}

/**
 * Товар «на исходе»: хотя бы одна фасовка ушла ниже своего порога.
 *
 * Флага на уровне товара сервер не отдаёт намеренно — «мало» это свойство
 * КОНКРЕТНОЙ фасовки (100 пачек по 50 г и две по 100 г — разные новости).
 * Правило свёртки живёт здесь одной функцией, чтобы колонка списка и чипсы
 * фильтра не разъехались.
 */
export function isProductLowStock(product: CatalogProduct): boolean {
  if (product.stockTracked === false) return false;
  return (product.availableBySku ?? []).some((s) => s.isLow);
}

/**
 * Товар кончился: складской учёт ведётся, а доступного нет.
 * Зеркало серверного фильтра `stock=out` — нужен для живых счётчиков чипсов
 * без повторного запроса на каждое переключение.
 */
export function isProductOutOfStock(product: CatalogProduct): boolean {
  if (product.stockTracked === false) return false;
  return Number(product.available ?? 0) <= 0;
}

export type ProductItemKindValue =
  | "RAW_MATERIAL"
  | "WORK_IN_PROGRESS"
  | "FINISHED_GOOD"
  | "PACKAGING"
  | "SERVICE"
  | "DIGITAL"
  | "BUNDLE";

/** Характеристика товара (phase19.16 Р2а, SB33): строка таблицы на странице товара. */
export interface ProductAttribute {
  /** Название, до 60 символов: «Вес нетто». */
  key: string;
  /** Значение, до 200 символов: «50 г». */
  value: string;
}

export interface CreateProductInput extends SeoKeywordsInput, RedirectOnSlugInput {
  categoryId: string;
  displayName: string;
  slug?: string;
  shortDescription?: string;
  description?: string;
  /** `null` — очистить заголовок для поиска (TD-634). */
  metaTitle?: string | null;
  metaDescription?: string;
  composition?: string;
  shelfLifeDays?: number;
  storageConditions?: string;
  allergens?: string[];
  nutritionFacts?: unknown;
  badWarning?: boolean;
  ageRestriction?: number;
  notForConsumption?: boolean;
  /** `null` — снять производителя (правка); не задан — не трогать. */
  producerId?: string | null;
  declarationNumber?: string;
  tagIds?: string[];
  /**
   * phase19.16 Р2а (SB33) — характеристики «название — значение» по порядку;
   * `null` — снять все. Сервер хранит как есть в `Product.attributes` (Json).
   */
  attributes?: ProductAttribute[] | null;
  /** phase14.0.2 — tristate-флаг публикации (null = наследовать). */
  isPublishedOnSite?: boolean | null;
  /** phase14.0.2 — роль в производстве (в UI «Роль в производстве»). */
  itemKind?: ProductItemKindValue | null;
  /**
   * phase19.22 (W22) — свой вид товара; null — «как в категории» (422
   * `product_kind_required`, если у категории вида нет).
   */
  productKindId?: string | null;
}

export type UpdateProductInput = Partial<CreateProductInput>;

export const catalogProducts = {
  /**
   * Список товаров. `withStock` добавляет сводку «Доступно» (W9) — один
   * groupBy на странице, поэтому запрашиваем её всегда, когда колонка видна.
   * `stock` — серверный фильтр наличия (см. `product-stock.service.ts`).
   */
  list: (params?: { withStock?: boolean; stock?: "out" | "low" }) => {
    const q = new URLSearchParams();
    if (params?.withStock) q.set("withStock", "1");
    if (params?.stock) q.set("stock", params.stock);
    const qs = q.toString();
    return apiFetch<CatalogProduct[]>(`/admin/catalog/products${qs ? `?${qs}` : ""}`);
  },
  findOne: (id: string) => apiFetch<CatalogProduct>(`/admin/catalog/products/${id}`),
  create: (input: CreateProductInput) =>
    apiFetch<CatalogProduct>("/admin/catalog/products", { method: "POST", json: input }),
  update: (id: string, input: UpdateProductInput) =>
    apiFetch<CatalogProduct>(`/admin/catalog/products/${id}`, {
      method: "PATCH",
      json: input,
    }),
  updateStatus: (id: string, status: ProductStatus) =>
    apiFetch<CatalogProduct>(`/admin/catalog/products/${id}/status`, {
      method: "PATCH",
      json: { status },
    }),
  archive: (id: string) =>
    apiFetch<CatalogProduct>(`/admin/catalog/products/${id}`, { method: "DELETE" }),
};

// ─── SKU ─────────────────────────────────────────────────────────────────

export type ChestnyznakState = "MARKED" | "PENDING" | "EXEMPT";

export interface CatalogSku {
  id: string;
  productId: string;
  code: string;
  displayName: string | null;
  saleQuantity: string;
  saleUnitId: string;
  minOrderStep: number;
  weightG: string | null;
  lengthMm: string | null;
  widthMm: string | null;
  heightMm: string | null;
  packageWeightG: string | null;
  /**
   * Цена продажи; null — роль цен продажи не видит (кладовщик, SC51,
   * контракт §11). Такое значение показываем «—» и обратно не отправляем.
   */
  priceGross: string | null;
  vatRate: number | null;
  vatIncluded: boolean;
  currency: string;
  stockQuantity: string;
  outOfStock: boolean;
  harvestYear: number | null;
  originRegion: string | null;
  attributes: unknown;
  gtin: string | null;
  tnved: string | null;
  chestnyznakState: ChestnyznakState;
  /** false — фасовка выведена из оборота (soft-delete, phase19.13). */
  isActive: boolean;
  /** ISO-момент вывода из оборота; null — фасовка активна. */
  archivedAt: string | null;
  /** phase19.14 (W14) — ячейка размещения по умолчанию («куда положить»). */
  defaultBinId: string | null;
  /** phase19.14 (W9) — порог «мало» для подсветки; null — только ноль красный. */
  minStock: string | null;
  /** phase19.22 (W17-1) — код склада `CHB` + 9 цифр + Луна; выдаёт база. */
  internalCode: string;
  /** phase19.22 (W17-5b) — свой шаблон этикетки; null — как у вида. */
  labelTemplate: LabelTemplate | null;
  /** Действующий шаблон: свой ?? вида ?? STANDARD. */
  effectiveLabelTemplate: LabelTemplate;
  /** Откуда действующий шаблон — подпись «Стандарт (из вида)». */
  labelTemplateSource: "sku" | "kind" | "default";
  createdAt: string;
  updatedAt: string;
}

export interface CreateSkuInput {
  code: string;
  displayName?: string;
  saleQuantity: number;
  saleUnitId: string;
  minOrderStep?: number;
  weightG?: number;
  lengthMm?: number;
  widthMm?: number;
  heightMm?: number;
  packageWeightG?: number;
  priceGross: number;
  vatRate?: number;
  vatIncluded?: boolean;
  currency?: string;
  stockQuantity?: number;
  outOfStock?: boolean;
  harvestYear?: number;
  originRegion?: string;
  attributes?: unknown;
  gtin?: string;
  tnved?: string;
  chestnyznakState?: ChestnyznakState;
  /** phase19.14 (W14) — ячейка по умолчанию; `null` снимает размещение. */
  defaultBinId?: string | null;
  /** phase19.14 (W9) — порог «мало»; `null` сбрасывает порог. */
  minStock?: number | null;
  /** phase19.22 (W17-5b) — свой шаблон этикетки; `null` — как у вида. */
  labelTemplate?: LabelTemplate | null;
}

export type UpdateSkuInput = Partial<CreateSkuInput>;

/**
 * Итог удаления фасовки (phase19.13): физически удалили или отправили в архив.
 *
 * Фасовку со складской историей или строками заказов стирать нельзя (ФЗ-402
 * ст.9), поэтому сервер переводит её в `isActive=false`. `message` — готовый
 * русский текст для тоста, решение «удалили или заархивировали» принимает
 * сервер, а не админка.
 */
export interface RemoveSkuResult {
  archived: boolean;
  message: string;
  /** Архивная запись для обновления списка; при физическом удалении — null. */
  sku: CatalogSku | null;
}

export const catalogSkus = {
  list: (productId: string) =>
    apiFetch<CatalogSku[]>(`/admin/catalog/products/${productId}/skus`),
  create: (productId: string, input: CreateSkuInput) =>
    apiFetch<CatalogSku>(`/admin/catalog/products/${productId}/skus`, {
      method: "POST",
      json: input,
    }),
  update: (productId: string, skuId: string, input: UpdateSkuInput) =>
    apiFetch<CatalogSku>(`/admin/catalog/products/${productId}/skus/${skuId}`, {
      method: "PATCH",
      json: input,
    }),
  remove: (productId: string, skuId: string) =>
    apiFetch<RemoveSkuResult>(`/admin/catalog/products/${productId}/skus/${skuId}`, {
      method: "DELETE",
    }),
  /**
   * Возврат фасовки из архива (phase19.13). Ошибки сервера:
   * 409 `sku_not_archived` — фасовка и так активна;
   * 409 `sku_product_archived` — сначала нужно восстановить сам товар.
   */
  restore: (productId: string, skuId: string) =>
    apiFetch<RestoreSkuResult>(
      `/admin/catalog/products/${productId}/skus/${skuId}/restore`,
      { method: "POST" },
    ),
};

/** Итог восстановления фасовки: обновлённая запись + текст тоста от сервера. */
export interface RestoreSkuResult {
  sku: CatalogSku;
  message: string;
}

// ─── Media ───────────────────────────────────────────────────────────────

export interface ProductMedia {
  id: string;
  productId: string;
  type: "IMAGE" | "VIDEO" | "DOC";
  fileKey: string;
  fileMimeType: string;
  sizeBytes: number;
  isPrimary: boolean;
  sortOrder: number;
  alt: string | null;
  url: string;
  createdAt: string;
  updatedAt: string;
}

export const catalogMedia = {
  list: (productId: string) =>
    apiFetch<ProductMedia[]>(`/admin/catalog/products/${productId}/media`),
  upload: async (productId: string, file: File, alt?: string): Promise<ProductMedia> => {
    const formData = new FormData();
    formData.append("file", file);
    if (alt) formData.append("alt", alt);
    const response = await fetch(`/api/v1/admin/catalog/products/${productId}/media`, {
      method: "POST",
      credentials: "include",
      // Кабинет вкладки (SC21): multipart идёт мимо apiFetch.
      headers: tabContextHeaders(`/admin/catalog/products/${productId}/media`),
      body: formData,
    });
    if (!response.ok) {
      const text = await response.text();
      let payload: { error?: { code: string; message: string } } = {};
      try {
        payload = JSON.parse(text);
      } catch {
        // ignore JSON parse errors
      }
      throw new Error(payload.error?.message ?? `Upload failed (${response.status})`);
    }
    const result = (await response.json()) as { data: ProductMedia };
    return result.data;
  },
  reorder: (
    productId: string,
    items: Array<{ id: string; sortOrder?: number; isPrimary?: boolean; alt?: string }>,
  ) =>
    apiFetch<ProductMedia[]>(`/admin/catalog/products/${productId}/media`, {
      method: "PATCH",
      json: { items },
    }),
  remove: (productId: string, mediaId: string) =>
    apiFetch<void>(`/admin/catalog/products/${productId}/media/${mediaId}`, {
      method: "DELETE",
    }),
};

// ─── Producers + ProducerCertificates ────────────────────────────────────

/** Вид документа производителя — из shared (= enum `CertificateType` Prisma, TD-486). */
export type CertificateType = CertificateTypeKey;

export interface CatalogProducer {
  id: string;
  shopId: string;
  displayName: string;
  legalAddress: string;
  inn: string | null;
  ogrn: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  notes: string | null;
  isActive: boolean;
  productsCount?: number;
  certificatesCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProducerInput {
  displayName: string;
  legalAddress: string;
  inn?: string;
  ogrn?: string;
  contactPhone?: string;
  contactEmail?: string;
  notes?: string;
}

export interface UpdateProducerInput extends Partial<CreateProducerInput> {
  isActive?: boolean;
}

export const catalogProducers = {
  list: () => apiFetch<CatalogProducer[]>("/admin/catalog/producers"),
  findOne: (id: string) => apiFetch<CatalogProducer>(`/admin/catalog/producers/${id}`),
  create: (input: CreateProducerInput) =>
    apiFetch<CatalogProducer>("/admin/catalog/producers", { method: "POST", json: input }),
  update: (id: string, input: UpdateProducerInput) =>
    apiFetch<CatalogProducer>(`/admin/catalog/producers/${id}`, {
      method: "PATCH",
      json: input,
    }),
  /** force=true для soft-delete (если есть привязанные товары). */
  remove: (id: string, force = false) =>
    apiFetch<{ deleted: boolean; deactivated: boolean }>(
      `/admin/catalog/producers/${id}${force ? "?force=true" : ""}`,
      { method: "DELETE" },
    ),
};

export interface ProducerCertificate {
  id: string;
  producerId: string;
  type: CertificateType;
  number: string;
  issuedBy: string | null;
  issuedAt: string | null;
  validUntil: string | null;
  fileKey: string | null;
  fileMimeType: string | null;
  public: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * phase19.16, партия «Юридические документы», шаг В (SB141;
   * uiux-legal-steps-b-c §6): файл сертификата. `undefined` — сервер поле ещё
   * не отдаёт (тогда наличие файла — по `fileKey`).
   */
  file?: ProducerCertificateFile | null;
  /** Как документ виден на сайте (`certificateSiteStatus` сервера); `undefined` — считается в кабинете. */
  siteStatus?: CertificateSiteStatus;
  /** Адрес файла на сайте, если он там виден; null — не виден. */
  publicFileUrl?: string | null;
}

/** Файл сертификата: имя, размер и тип (PDF, JPG, PNG). */
export interface ProducerCertificateFile {
  name: string;
  sizeBytes: number;
  mime: string;
  /** Когда загружен; может не прийти. */
  uploadedAt?: string | null;
}

/** Тон статуса сертификата (§6.1): нейтральный, ошибка, внимание, успех. */
export type CertificateSiteTone = "neutral" | "error" | "warning" | "success";

/** Статус сертификата на сайте: код, подпись и тон (§6.1, шесть состояний). */
export interface CertificateSiteStatus {
  code: "producer_inactive" | "hidden" | "expired" | "no_file" | "expiring" | "on_site";
  label: string;
  tone: CertificateSiteTone;
  /** Файл виден на странице «Сертификаты и декларации» (сервер; в запасном расчёте — нет). */
  fileOnSite?: boolean;
  /** Номер виден на карточке товара. */
  numberOnSite?: boolean;
}

export interface CreateProducerCertificateInput {
  type: CertificateType;
  number: string;
  issuedBy?: string;
  issuedAt?: string;
  validUntil?: string;
  public?: boolean;
}

export type UpdateProducerCertificateInput = Partial<CreateProducerCertificateInput>;

export const catalogProducerCertificates = {
  list: (producerId: string) =>
    apiFetch<ProducerCertificate[]>(`/admin/catalog/producers/${producerId}/certificates`),
  create: (producerId: string, input: CreateProducerCertificateInput) =>
    apiFetch<ProducerCertificate>(`/admin/catalog/producers/${producerId}/certificates`, {
      method: "POST",
      json: input,
    }),
  update: (producerId: string, certId: string, input: UpdateProducerCertificateInput) =>
    apiFetch<ProducerCertificate>(
      `/admin/catalog/producers/${producerId}/certificates/${certId}`,
      { method: "PATCH", json: input },
    ),
  remove: (producerId: string, certId: string) =>
    apiFetch<void>(`/admin/catalog/producers/${producerId}/certificates/${certId}`, {
      method: "DELETE",
    }),
  /** Шаг В (SB141): загрузить или заменить файл (PDF, JPG, PNG ≤ 20 МБ) с процентом. */
  uploadFile: (producerId: string, certId: string, file: File, onProgress?: (percent: number) => void) => {
    const form = new FormData();
    form.append("file", file);
    // 413 от прокси приходит HTML-страницей — код по статусу (текст — certificateUploadErrorText).
    return uploadWithProgress<ProducerCertificate>(`/admin/catalog/producers/${producerId}/certificates/${certId}/file`, form, {
      onProgress,
      fallbackCodes: { 413: "producer_cert_file_too_large" },
    });
  },
  /** Убрать файл: на карточке товара останется номер. */
  removeFile: (producerId: string, certId: string) =>
    apiFetch<ProducerCertificate>(`/admin/catalog/producers/${producerId}/certificates/${certId}/file`, { method: "DELETE" }),
  /** Скачать файл для просмотра в кабинете (скрытый и просроченный — тоже). */
  downloadFile: (producerId: string, certId: string) =>
    apiDownload(`/admin/catalog/producers/${producerId}/certificates/${certId}/file`, {}, "certificate"),
};

// ─── Suppliers ───────────────────────────────────────────────────────────

export interface CatalogSupplier {
  id: string;
  shopId: string;
  displayName: string;
  inn: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  defaultDeliveryDays: number | null;
  notes: string | null;
  isActive: boolean;
  costsCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSupplierInput {
  displayName: string;
  inn?: string;
  contactPhone?: string;
  contactEmail?: string;
  defaultDeliveryDays?: number;
  notes?: string;
}

export interface UpdateSupplierInput extends Partial<CreateSupplierInput> {
  isActive?: boolean;
}

export const catalogSuppliers = {
  list: () => apiFetch<CatalogSupplier[]>("/admin/catalog/suppliers"),
  findOne: (id: string) => apiFetch<CatalogSupplier>(`/admin/catalog/suppliers/${id}`),
  create: (input: CreateSupplierInput) =>
    apiFetch<CatalogSupplier>("/admin/catalog/suppliers", { method: "POST", json: input }),
  update: (id: string, input: UpdateSupplierInput) =>
    apiFetch<CatalogSupplier>(`/admin/catalog/suppliers/${id}`, {
      method: "PATCH",
      json: input,
    }),
  remove: (id: string, force = false) =>
    apiFetch<{ deleted: boolean; deactivated: boolean }>(
      `/admin/catalog/suppliers/${id}${force ? "?force=true" : ""}`,
      { method: "DELETE" },
    ),
};

// ─── ProductCost ─────────────────────────────────────────────────────────

export interface ProductCost {
  id: string;
  productId: string;
  costPrice: string | null;
  costCurrency: string | null;
  supplierId: string | null;
  internalCode: string | null;
  notes: string | null;
  updatedAt: string;
}

export interface UpsertCostInput {
  costPrice?: number;
  costCurrency?: string;
  /** `null` — снять поставщика. */
  supplierId?: string | null;
  internalCode?: string;
  notes?: string;
}

export const catalogCost = {
  find: (productId: string) =>
    apiFetch<ProductCost | null>(`/admin/catalog/products/${productId}/cost`),
  upsert: (productId: string, input: UpsertCostInput) =>
    apiFetch<ProductCost>(`/admin/catalog/products/${productId}/cost`, {
      method: "PUT",
      json: input,
    }),
  remove: (productId: string) =>
    apiFetch<void>(`/admin/catalog/products/${productId}/cost`, { method: "DELETE" }),
};

// ─── Promotions (phase18.3, promo.md P1-P3) ───────────────────────────────

export type PromotionKind = "PERCENT" | "FIXED";
export type PromotionScope = "CATALOG" | "CATEGORY" | "PRODUCT" | "SKU";

export interface CatalogPromotion {
  id: string;
  shopId: string;
  displayName: string;
  kind: PromotionKind;
  value: number;
  scopeType: PromotionScope;
  categoryId: string | null;
  productId: string | null;
  skuId: string | null;
  /** null = действует на всех каналах продаж. */
  channel: string | null;
  startsAt: string;
  /** null = бессрочно. */
  endsAt: string | null;
  isActive: boolean;
  /** Действует ли прямо сейчас (isActive + окно дат) — считает сервер. */
  isRunning: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePromotionInput {
  displayName: string;
  kind: PromotionKind;
  value: number;
  scopeType: PromotionScope;
  categoryId?: string;
  productId?: string;
  skuId?: string;
  channel?: string | null;
  startsAt: string;
  endsAt?: string | null;
  isActive?: boolean;
}

export type UpdatePromotionInput = Partial<CreatePromotionInput>;

export const catalogPromotions = {
  list: () => apiFetch<CatalogPromotion[]>("/admin/catalog/promotions"),
  create: (input: CreatePromotionInput) =>
    apiFetch<CatalogPromotion>("/admin/catalog/promotions", { method: "POST", json: input }),
  update: (id: string, input: UpdatePromotionInput) =>
    apiFetch<CatalogPromotion>(`/admin/catalog/promotions/${id}`, {
      method: "PATCH",
      json: input,
    }),
  remove: (id: string) =>
    apiFetch<void>(`/admin/catalog/promotions/${id}`, { method: "DELETE" }),
};
